import { RemoveYear, hasInErrorReason, } from "./helpers.js";
import { noDataPlaceholder } from "./constants.js";
class ClaimsSearch {
    constructor() {
        this.format = (response) => {
            const searchMetadata = response?.searchResultMetadata || {};
            const resultMetadata = searchMetadata?.resultMetaData ||
                searchMetadata?.resultMetadata ||
                {};
            const searchResultMetadata = {
                startIndex: searchMetadata?.startIndex ?? 0,
                totalResults: searchMetadata?.totalResults ?? 0,
                itemsPerPage: searchMetadata?.itemsPerPage ?? 0,
                timeTaken: resultMetadata?.timeTaken ?? noDataPlaceholder,
                links: resultMetadata?._links ?? {},
            };
            const claimsSearch = [];
            const getValueOrDefault = (value, defaultValue = noDataPlaceholder) => (typeof value === "number" || !!(value && value.trim())) ? value : defaultValue;
            const getDigitalAddressEndDatePriority = (digitalAddressEndDt) => {
                if (!digitalAddressEndDt) {
                    return 0;
                }
                const parsedEndDate = new Date(digitalAddressEndDt);
                if (Number.isNaN(parsedEndDate.getTime())) {
                    return 4;
                }
                const normalizedEndDate = new Date(parsedEndDate.getFullYear(), parsedEndDate.getMonth(), parsedEndDate.getDate());
                const today = new Date();
                const startOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate());
                if (normalizedEndDate > startOfToday) {
                    return 1;
                }
                if (normalizedEndDate.getTime() === startOfToday.getTime()) {
                    return 2;
                }
                return 3;
            };
            const getHomeDigitalAddressText = (digitalAddresses = []) => {
                const homeDigitalAddress = digitalAddresses
                    .filter((d) => d.digitalAddressTypeCd === "HE" || d.digitalAddressType === "Personal Email")
                    .reduce((bestMatch, currentAddress) => {
                    if (!bestMatch) {
                        return currentAddress;
                    }
                    return getDigitalAddressEndDatePriority(currentAddress.digitalAddressEndDt) <
                        getDigitalAddressEndDatePriority(bestMatch.digitalAddressEndDt)
                        ? currentAddress
                        : bestMatch;
                }, null);
                const selectedAddressPriority = homeDigitalAddress
                    ? getDigitalAddressEndDatePriority(homeDigitalAddress.digitalAddressEndDt)
                    : 4;
                return selectedAddressPriority <= 2
                    ? homeDigitalAddress?.digitalAddressText ?? noDataPlaceholder
                    : noDataPlaceholder;
            };
            const getAddressData = (claim) => {
                const postalAddresses = claim?.claimantDetails?.postalAddresses || [];
                const addressTypeToFilter = claim?.claimSystemCd === "FNEOS" ? "Home" : "PA";
                const filteredAddresses = postalAddresses.filter((address) => address.addressTypeCd === addressTypeToFilter);
                if (filteredAddresses.length === 0) {
                    return null;
                }
                return filteredAddresses.reduce((latest, current) => new Date(current.beginDate) > new Date(latest.beginDate) ? current : latest);
            };
            if (response.claims && Array.isArray(response.claims)) {
                response.claims.forEach((claim) => {
                    if (hasInErrorReason(claim?.claimProcessingStage, claim?.customClaimStatusReason, claim?.claimStatusReason, claim?.claimStatusReasonDesc, claim?.claimStatusReasonCdDesc, claim?.customClaimStatusReasonCdDesc))
                        return;
                    const paymentDetails = claim?.claimPaymentsSummaryDetails || {};
                    const recentPaymentDetails = paymentDetails.recentPaymentDetails || {};
                    const claimantDetails = claim?.claimantDetails || {};
                    const coverageDetails = claim?.coverageDetails || {};
                    const disabilityDetails = claim?.disabilityDetails || {};
                    const employeeInformation = claim?.employeeInformation || {};
                    const addressData = getAddressData(claim);
                    const claimData = {
                        paymentPeriodEndDt: getValueOrDefault(recentPaymentDetails.paymentPeriodEndDt),
                        paymentRecentCheckAmt: getValueOrDefault(recentPaymentDetails.paymentRecentCheckAmt),
                        paymentGrossBenefitAmt: getValueOrDefault(recentPaymentDetails.paymentGrossBenefitAmt),
                        paymentDeductionsAmt: getValueOrDefault(recentPaymentDetails.paymentDeductionsAmt),
                        paymentOffsetsAmt: getValueOrDefault(recentPaymentDetails.paymentOffsetsAmt),
                        paymentEndDt: getValueOrDefault(recentPaymentDetails.paymentIssueDt),
                        workerCompensationOffsetsAmt: getValueOrDefault(recentPaymentDetails.workerCompensationOffsetsAmt),
                        nonPaymentStartDt: getValueOrDefault(recentPaymentDetails.nonPaymentStartDt),
                        nonPaymentEndDt: getValueOrDefault(recentPaymentDetails.nonPaymentEndDt),
                        socialSecurityOffsetAmt: getValueOrDefault(recentPaymentDetails.socialSecurityOffsetAmt),
                        otherOffsetsAmt: getValueOrDefault(recentPaymentDetails.otherOffsetsAmt),
                        monthlyCoveredSalaryAmt: getValueOrDefault(coverageDetails.monthlyCoveredSalaryAmt),
                        paymentAmt: getValueOrDefault(paymentDetails.paymentAmt),
                        monthsPaid: getValueOrDefault(paymentDetails.monthsPaid),
                        checkNumber: getValueOrDefault(recentPaymentDetails.checkNumber),
                        claimId: getValueOrDefault(claim?.claimId),
                        legacyClaimNumber: getValueOrDefault(claim?.aliasClaimId),
                        claimSystemCd: getValueOrDefault(claim?.claimSystemCd),
                        totalPaidToDtAmt: getValueOrDefault(paymentDetails.totalPaidToDtAmt),
                        totalDaysPaidCount: getValueOrDefault(paymentDetails.totalDaysPaidCount),
                        claimDaysPaidCount: getValueOrDefault(paymentDetails.claimDaysPaidCount),
                        claimStatus: getValueOrDefault(claim?.derivedClaimStatusDesc),
                        claimStatusReason: getValueOrDefault(claim?.customClaimStatusReason),
                        claimSubmissionSource: getValueOrDefault(claim?.claimSubmissionSource),
                        statusEffectiveDt: getValueOrDefault(claim?.claimStatusEffectiveDt),
                        claimApprovedThruDt: getValueOrDefault(claim?.claimApprovedThruDt),
                        claimBenefitBeginDt: getValueOrDefault(claim?.claimBenefitBeginDt),
                        claimBenefitEndDt: getValueOrDefault(claim?.claimBenefitEndDt),
                        benefitResumeDt: getValueOrDefault(claim?.benefitResumeDate),
                        claimPaidThroughDate: getValueOrDefault(claim?.claimPaidThroughDate),
                        claimSocialSecurityAwardDt: getValueOrDefault(paymentDetails.claimSocialSecurityAwardDt),
                        claimClosedDt: getValueOrDefault(claim?.claimClosedDt),
                        eliminationPeriodEndDate: getValueOrDefault(claim?.eliminationPeriodEndDate),
                        workIncentiveBenefitInd: getValueOrDefault(paymentDetails.workIncentiveBenefitInd),
                        claimMaxDurationDt: getValueOrDefault(claim?.claimMaxDurationDt),
                        receivedDt: getValueOrDefault(claim?.claimReceivedTs),
                        fullName: getValueOrDefault(claimantDetails.claimantDisplayName),
                        firstName: getValueOrDefault(claimantDetails.firstName),
                        lastName: getValueOrDefault(claimantDetails.lastName),
                        dateOfBirth: getValueOrDefault(claimantDetails.dateOfBirth),
                        genderAtBirth: getValueOrDefault(claimantDetails.genderAtBirth),
                        postalCode: (addressData?.postalCd || null),
                        hireDt: getValueOrDefault(claimantDetails.hireDt),
                        jobTitle: getValueOrDefault(claimantDetails.jobTitleName),
                        personDisplayName: getValueOrDefault(claim?.claimOwner?.personDisplayName),
                        personID: getValueOrDefault(claim?.claimOwner?.personID),
                        digitalAddresses: claimantDetails.digitalAddresses,
                        digitalAddressText: getHomeDigitalAddressText(claimantDetails.digitalAddresses),
                        taxIdentificationNumber: getValueOrDefault(claimantDetails.taxIdentificationNumber),
                        employeeId: getValueOrDefault(claimantDetails.employeeId),
                        divisionId: getValueOrDefault(coverageDetails.divisionId),
                        coverageSourceDesc: getValueOrDefault(coverageDetails.customCoverageDesc),
                        divisionName: getValueOrDefault(coverageDetails.divisionName),
                        weeklyCoveredSalaryAmt: getValueOrDefault(coverageDetails.weeklyCoveredSalaryAmt),
                        ReportingLocation: getValueOrDefault(employeeInformation.employeeLocationName),
                        eligibilityGroup: getValueOrDefault(coverageDetails.eligibilityGroup),
                        eligibilityGroupDesc: getValueOrDefault(coverageDetails.eligibilityGroupDesc),
                        groupAgreementId: getValueOrDefault(coverageDetails.groupAgreementID),
                        organizationUnitId: getValueOrDefault(coverageDetails.organizationUnitId),
                        choiceCd: getValueOrDefault(coverageDetails.choiceCd),
                        empContributionPremiumAmt: getValueOrDefault(coverageDetails.empContributionPremiumAmt),
                        coverageType: getValueOrDefault(claim?.claimCoverageTypeCd),
                        disabilityBeginDt: RemoveYear(getValueOrDefault(disabilityDetails.disabilityBeginDt)),
                        lagDays: getValueOrDefault(disabilityDetails.lagDays),
                        primaryDiagnosisCd: getValueOrDefault(disabilityDetails.primaryDiagnosisCd),
                        primaryDiagnosisDesc: getValueOrDefault(disabilityDetails.primaryDiagnosisDesc),
                        secondaryDiagnosisCd: getValueOrDefault(disabilityDetails.secondaryDiagnosisCd),
                        secondaryDiagnosisDesc: getValueOrDefault(disabilityDetails.secondaryDiagnosisDesc),
                        workLevel: getValueOrDefault(disabilityDetails.workLevel),
                        workRelatedInd: getValueOrDefault(disabilityDetails.workRelatedInd),
                        actualDeliveryDt: RemoveYear(getValueOrDefault(disabilityDetails.actualDeliveryDt)),
                        lastDateWorked: getValueOrDefault(disabilityDetails.lastWorkedDt),
                        partTimeRTWDt: RemoveYear(getValueOrDefault(disabilityDetails.partTimeRtwDt)),
                        fullTimeRTWDt: RemoveYear(getValueOrDefault(disabilityDetails.fullTimeRtwDt)),
                        takeoverClaimInd: getValueOrDefault(claim?.takeoverClaimInd),
                        claimDecisionDt: getValueOrDefault(claim?.claimDecisionDt),
                        employeeContributionPostTaxPct: getValueOrDefault(claim?.employeeContributionPostTaxPct),
                        ficaAmt: getValueOrDefault(recentPaymentDetails.ficaAmt),
                        firstPaymentDt: getValueOrDefault(paymentDetails.firstPaymentDt),
                        healthCareCreditAmt: getValueOrDefault(recentPaymentDetails.healthCareCreditAmt),
                        totalHealthCareCreditAmt: getValueOrDefault(paymentDetails.totalHealthCareCreditAmt),
                        lostDays: getValueOrDefault(disabilityDetails.lostDays),
                        payGroupNbr: getValueOrDefault(claim?.payGroupNbr),
                        projectedFullTimeRtwDt: getValueOrDefault(disabilityDetails.projectedFullTimeRtwDt),
                        projectedPartTimeRtwDt: getValueOrDefault(disabilityDetails.projectedPartTimeRtwDt),
                        socialSecurityOffsetInd: getValueOrDefault(paymentDetails.socialSecurityOffsetInd),
                        stateOffsetAmt: getValueOrDefault(recentPaymentDetails.stateOffsetAmt),
                        workersCompensationOffsetInd: getValueOrDefault(paymentDetails.workersCompensationOffsetInd),
                        age: getValueOrDefault(claimantDetails.age),
                        duration: getValueOrDefault(claim?.duration),
                        jobTitleName: getValueOrDefault(claimantDetails.jobTitleName),
                        paidDuration: getValueOrDefault(paymentDetails.paidDuration),
                        addressLineOne: addressData?.addressLineOne || null,
                        addressLineTwo: addressData?.addressLineTwo || null,
                        city: addressData?.city || null,
                        jurisdictionCode: addressData?.jurisdictionCode || null,
                        workState: getValueOrDefault(claim?.employeeWorkStateCd),
                    };
                    claimsSearch.push(claimData);
                });
            }
            return { claimsSearch, searchResultMetadata };
        };
    }
}
export default new ClaimsSearch();
